<script lang="ts" setup>
  import type { ICloseArgs } from '#shared/types/modal.types';
  import { ESignUpSteps } from '#shared/types/user.types';
  import DisplayNamePrompt from '~/components/signUp/DisplayNamePrompt.vue';
  import EmailConfirmation from '~/components/signUp/EmailConfirmation.vue';
  import SupportChat, { ZENDESK_LO_USER_ID } from '~/utils/services/support-chat.service';

  const emit = defineEmits<{ close: [ICloseArgs] }>();

  const closeButtonIsVisible = computed(() => {
    const filter: TSignUpSteps[] = [ESignUpSteps.Credentials, ESignUpSteps.EmailConfirmation];
    return filter.includes(stores.signUp.activeStep);
  });

  const { windowWidth } = useDeviceType();
  const { orientation } = useOrientation();

  const useFullscreen = computed(() => windowWidth.value < 480 && orientation.value === EOrientation.PORTRAIT);

  onMounted(async () => {
    subscribeWindowClose();

    SupportChat.hideButton();
    await nextTick();
    enableBodyPointerEvents();
  });

  onBeforeUnmount(() => {
    if (!stores.user.isLoggedIn) {
      if (SupportChat.getIsInitialized()) {
        SupportChat.toggle();
      } else {
        SupportChat.init(ZENDESK_LO_USER_ID)
          .then(() => SupportChat.toggle())
          .catch((error: Error) => {
            console.error('ZenDesk LO Init error', error);
          });
      }
    }

    stores.signUp.reset();
    unsubscribeWindowClose();
  });

  watch(
    () => stores.signUp.activeStep,
    () => {
      if (ESignUpSteps.EmailConfirmation === stores.signUp.activeStep) {
        unsubscribeWindowClose();
      }
    },
  );

  function enableBodyPointerEvents() {
    // By default, UModal sets pointer-events: "none" for the body, it must be set to "auto" for ReCaptcha to work correctly
    document.body.style.pointerEvents = 'auto';
  }

  async function closeModal() {
    handleWindowClose();
    emit('close', {});
  }

  function handleWindowClose() {
    if (stores.signUp.activeStep === ESignUpSteps.EmailConfirmation) {
      stores.signUp.handleSignOut();
    }
  }

  function subscribeWindowClose() {
    window.addEventListener('beforeunload', handleWindowClose);
  }

  function unsubscribeWindowClose() {
    window.removeEventListener('beforeunload', handleWindowClose);
  }
</script>

<template>
  <UModal
    title="Sign Up Modal"
    description="Sign up to the platform"
    :ui="{ content: 'overflow-y-hidden w-full max-w-[460px]' }"
    :fullscreen="useFullscreen"
  >
    <template #content>
      <div class="overflow-y-auto">
        <NuxtImg
          src="/images/sign-up/MD-sign-up-banner-02.png"
          class="w-full h-full max-h-[140px]"
          height="140"
          format="webp"
          densities="x1 x2"
        />
        <Icon
          v-if="closeButtonIsVisible"
          class="size-6 absolute text-white top-2.5 right-2.5 cursor-pointer rotate-45"
          name="i-lucide:plus"
          @click="closeModal"
        />
        <div class="flex flex-col items-center justify-start gap-6 px-7 pt-4 pb-6">
          <SignUpCredentials v-if="stores.signUp.activeStep === ESignUpSteps.Credentials" />
          <EmailConfirmation v-if="stores.signUp.activeStep === ESignUpSteps.EmailConfirmation" />
          <DisplayNamePrompt v-if="stores.signUp.activeStep === ESignUpSteps.DisplayNamePrompt" />
        </div>
      </div>
    </template>
  </UModal>
</template>
